import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../hooks/useAuth';
import { CreditCard, Zap, Calendar, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { SubNav } from '@/components/ui/sub-nav';

const API_URL = import.meta.env.VITE_API_URL || 'https://api.frolic.so';

function BillingPage() {
  const { accessToken } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['billing'],
    queryFn: async () => {
      const response = await fetch(`${API_URL}/v1/admin/billing`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Failed to fetch billing');
      return response.json();
    },
  });

  const usage = data?.usage;

  const subNavItems = [
    { to: '/admin', label: 'Teams' },
    { to: '/admin/settings', label: 'Settings' },
    { to: '/admin/billing', label: 'Billing' },
    { to: '/admin/audit', label: 'Audit' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Billing & Usage</h1>
        <p className="text-muted-foreground">Manage your subscription and view usage</p>
      </div>

      <SubNav items={subNavItems} />

      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      )}

      {usage && (
        <div className="space-y-6">
          <Card className="border-primary/20 bg-primary/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20">
                    <Zap className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Current Plan</p>
                    <p className="text-2xl font-bold capitalize text-foreground">{usage.plan}</p>
                  </div>
                </div>
                <Button>
                  <CreditCard className="mr-2 h-4 w-4" />
                  Upgrade Plan
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Projects
                </CardTitle>
                <Badge variant="secondary">
                  {Math.round((usage.projects / usage.limits.maxProjects) * 100)}%
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  {usage.projects} / {usage.limits.maxProjects}
                </div>
                <div className="mt-2 h-2 rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary transition-all"
                    style={{
                      width: `${Math.min((usage.projects / usage.limits.maxProjects) * 100, 100)}%`,
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  API Tokens
                </CardTitle>
                <Badge variant="secondary">
                  {Math.round((usage.apiTokens / usage.limits.maxTokens) * 100)}%
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  {usage.apiTokens} / {usage.limits.maxTokens}
                </div>
                <div className="mt-2 h-2 rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary transition-all"
                    style={{
                      width: `${Math.min((usage.apiTokens / usage.limits.maxTokens) * 100, 100)}%`,
                    }}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Events This Month
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  {(usage.currentPeriod?.eventsUsed || 0).toLocaleString()}
                </div>
                <p className="text-xs text-muted-foreground">
                  of {usage.limits.maxEventsPerMonth.toLocaleString()} events
                </p>
                <div className="mt-2 h-2 rounded-full bg-muted">
                  <div
                    className="h-2 rounded-full bg-primary transition-all"
                    style={{
                      width: `${Math.min(
                        ((usage.currentPeriod?.eventsUsed || 0) / usage.limits.maxEventsPerMonth) *
                          100,
                        100
                      )}%`,
                    }}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <div>
                  <CardTitle>Current Billing Period</CardTitle>
                  <CardDescription>Your usage resets at the end of each period</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg bg-muted p-4">
                  <p className="text-sm text-muted-foreground">Period Start</p>
                  <p className="text-lg font-medium text-foreground">
                    {new Date(usage.currentPeriod.start).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
                <div className="rounded-lg bg-muted p-4">
                  <p className="text-sm text-muted-foreground">Period End</p>
                  <p className="text-lg font-medium text-foreground">
                    {new Date(usage.currentPeriod.end).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/billing')({
  component: BillingPage,
});
